import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import joinClasses from 'webapp/helpers/join-classes';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanButtonComponent from 'webapp/components/common/upgrade-plan-button';

export interface Args {
	onClose: () => void;
}

export default class AppNotificationsBillingComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('flow/cancellation') declare flowCancellationService: Service.Flow.Cancellation;
	@service('billing') declare billingService: Service.Billing;

	trackingOrigin = 'top_banner';

	@tracked submitInProgress = false;

	get hasSubscriptionExpiredAndIsCanceled() {
		const { isExpired, isCanceled } = this.billingService;
		return isExpired && isCanceled;
	}

	get isAdmin() {
		return this.currentUserService.isAdmin;
	}

	get text() {
		const { isPaymentError, isPaused } = this.billingService;

		let text;
		if (isPaymentError) {
			const formattedFailureMessage = this.billingService.formattedFailureMessage,
				formattedFailureMessageText = formattedFailureMessage
					? ` (${formattedFailureMessage})`
					: '';
			text = `<strong>Your recent transaction has failed${formattedFailureMessageText}.</strong> Please update your payment details.`;
		} else if (isPaused) {
			text = this.currentUserService.isAdmin
				? 'Your subscription is currently paused. Any modifications will trigger reactivation of the subscription.'
				: 'Your subscription is currently on pause. Contact your admin to re-activate.';
		} else {
			text = '<strong>Your subscription has been cancelled.</strong> Data sync is on hold.';
		}

		return text;
	}

	get canPlanBeSelected() {
		return this.hasSubscriptionExpiredAndIsCanceled && !this.billingService.isPaymentError;
	}

	get buttonText() {
		if (this.billingService.isPaymentError) {
			return 'Update payment';
		} else if (this.billingService.isPaused) {
			return 'Re-activate';
		} else {
			return 'Purchase subscription';
		}
	}

	@action
	takeAction() {
		if (this.billingService.isPaused) {
			this.submitInProgress = true;

			void this.flowCancellationService.reactivateSubscription().finally(() => {
				this.submitInProgress = false;
			});
		} else {
			this.navigationService.transitionToRoute('settings.billing');
		}
	}
	<template>
		<div
			class={{joinClasses
				(array
					"notification-container"
					"billing-notification"
					(if this.hasSubscriptionExpiredAndIsCanceled "red")
				)
			}}
		>
			<div class="container {{unless this.isAdmin 'withoutButton'}}">
				<a role="button" class="close" {{on "click" @onClose}}>
					<CommonUiIconComponent @name="Close" @size="sm" />
				</a>

				<p>{{{this.text}}}</p>
				{{#if this.isAdmin}}
					{{#if this.canPlanBeSelected}}
						<CommonUpgradePlanButtonComponent
							@size="small"
							@trackingOrigin={{this.trackingOrigin}}
						/>
					{{else}}
						<a
							role="button"
							{{on "click" this.takeAction}}
							class="button theme-blue size-small"
						>
							{{this.buttonText}}
						</a>
					{{/if}}
				{{/if}}
			</div>
		</div>
	</template>
}
